import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pause, Play, Trash2, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { QueryStateBoundary } from '@/components/ui/query-state-boundary';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePmProperty } from '@/hooks/usePmProperty';
import {
  getTrustedOperators,
  createTrustedOperator,
  updateTrustedOperator,
  suspendTrustedOperator,
  reinstateTrustedOperator,
  deleteTrustedOperator,
} from '@/services/api.client';
import type { TrustedOperator, CreateTrustedOperatorBody } from '@/services/api.client';

import { TrustedOperatorDrawer } from './TrustedOperatorDrawer';

const TYPE_LABELS: Record<string, string> = {
  school_bus: 'School Bus',
  shuttle: 'Shuttle',
  service_provider: 'Service Provider',
  other: 'Other',
};

const TYPE_COLORS: Record<string, string> = {
  school_bus: 'bg-amber-100 text-amber-800',
  shuttle: 'bg-blue-100 text-blue-800',
  service_provider: 'bg-purple-100 text-purple-800',
  other: 'bg-gray-100 text-gray-800',
};

function formatWindows(windows: TrustedOperator['allowed_windows']): string {
  if (!windows || windows.length === 0) return '—';
  return windows.map((w) => `${w.days.join(', ')} ${w.from}–${w.until}`).join(' | ');
}

export function TrustedOperatorsPage() {
  const { t } = useTranslation();
  const pm = usePmProperty();
  const propertyId = pm?.propertyId ?? '';
  const queryClient = useQueryClient();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState<TrustedOperator | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TrustedOperator | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<TrustedOperator | null>(null);

  const operatorsQuery = useQuery({
    queryKey: ['trusted-operators', propertyId],
    queryFn: () => getTrustedOperators(propertyId),
    enabled: !!propertyId,
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateTrustedOperatorBody) => createTrustedOperator(propertyId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['trusted-operators', propertyId] });
      setDrawerOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CreateTrustedOperatorBody> }) =>
      updateTrustedOperator(propertyId, id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['trusted-operators', propertyId] });
      setDrawerOpen(false);
      setEditingOperator(null);
    },
  });

  const suspendMutation = useMutation({
    mutationFn: (id: string) => suspendTrustedOperator(propertyId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['trusted-operators', propertyId] });
      setSuspendTarget(null);
    },
  });

  const reinstateMutation = useMutation({
    mutationFn: (id: string) => reinstateTrustedOperator(propertyId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['trusted-operators', propertyId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTrustedOperator(propertyId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['trusted-operators', propertyId] });
      setDeleteTarget(null);
    },
  });

  const handleCreate = () => {
    setEditingOperator(null);
    setDrawerOpen(true);
  };

  const handleEdit = (op: TrustedOperator) => {
    setEditingOperator(op);
    setDrawerOpen(true);
  };

  const handleSubmit = (body: CreateTrustedOperatorBody) => {
    if (editingOperator) {
      updateMutation.mutate({ id: editingOperator.id, body });
    } else {
      createMutation.mutate(body);
    }
  };

  const operators = operatorsQuery.data ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">
            {t('trustedOperators.title', 'Trusted Operators')}
          </h1>
          <p className="text-sm text-[var(--text-tertiary)] mt-1">
            {t('trustedOperators.subtitle', 'Manage pre-approved operators for fast gate entry')}
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="size-4 mr-2" />
          {t('trustedOperators.addOperator', 'Add Operator')}
        </Button>
      </div>

      {/* Table */}
      <QueryStateBoundary
        query={operatorsQuery}
        emptyMessage={t(
          'trustedOperators.emptyTitle',
          'No trusted operators yet. Add one to get started.',
        )}
      >
        {() => (
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('trustedOperators.name', 'Name')}</TableHead>
                  <TableHead>{t('trustedOperators.type', 'Type')}</TableHead>
                  <TableHead>{t('trustedOperators.windows', 'Allowed Windows')}</TableHead>
                  <TableHead>{t('trustedOperators.dailyLimit', 'Daily Limit')}</TableHead>
                  <TableHead>{t('trustedOperators.notifyAll', 'Notify All')}</TableHead>
                  <TableHead>{t('trustedOperators.status', 'Status')}</TableHead>
                  <TableHead className="text-right">{t('common.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {operators.map((op) => (
                  <TableRow key={op.id}>
                    <TableCell className="font-medium">{op.name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={TYPE_COLORS[op.operator_type] ?? ''}>
                        {TYPE_LABELS[op.operator_type] ?? op.operator_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-[var(--text-secondary)] max-w-[200px] truncate">
                      {formatWindows(op.allowed_windows)}
                    </TableCell>
                    <TableCell>{op.max_daily_entries ?? '∞'}</TableCell>
                    <TableCell>{op.notify_all_on_entry ? '✓' : '—'}</TableCell>
                    <TableCell>
                      <Badge variant={op.status === 'active' ? 'default' : 'destructive'}>
                        {op.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(op)}
                          aria-label={t('common.edit', 'Edit')}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        {op.status === 'active' ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setSuspendTarget(op)}
                            aria-label={t('trustedOperators.suspend', 'Suspend')}
                          >
                            <Pause className="size-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => reinstateMutation.mutate(op.id)}
                            aria-label={t('trustedOperators.reinstate', 'Reinstate')}
                          >
                            <Play className="size-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteTarget(op)}
                          aria-label={t('common.delete', 'Delete')}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </QueryStateBoundary>

      {/* Create/Edit Drawer */}
      <TrustedOperatorDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        operator={editingOperator}
        onSubmit={handleSubmit}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      {/* Suspend Confirmation */}
      <AlertDialog open={!!suspendTarget} onOpenChange={() => setSuspendTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('trustedOperators.suspendTitle', 'Suspend operator?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'trustedOperators.suspendDescription',
                'Guards will not be able to grant entry for this operator while suspended.',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => suspendTarget && suspendMutation.mutate(suspendTarget.id)}
            >
              {t('trustedOperators.suspend', 'Suspend')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('trustedOperators.deleteTitle', 'Remove operator?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'trustedOperators.deleteDescription',
                'This will remove the operator from the gate list. Historical entries are preserved.',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
